// Animate It V4.8.1
// used for wiggling elements, mouse and scroll with interaction
// also copy _animate-it.scss

// init: animateIt = new AnimateIt();

// possible properties:
// frameFunction = '': external frame animation function using this frame handler
// blockSmoothScroll = false: blocks smoothscroll from running

// SMOOTH SCROLL ---------------------------------------------------------------
// adds smoothscroll with sticky support, supports 1 instance

// smoothScroll usage: animateIt.smoothScroll({});

// required properties:
// none

// possible properties:
// speed = 0.1: larger is faster
// bounce = 0.01: larger is less overschoot

// related methods:
// animateIt.setScrollPosHard({ pos: 0 }); immediately here, even if smooth is on
// animateIt.setScrollPosSoft({ pos: 0 }); use smoothscroll here
// animateIt.disableSmoothScroll(); disables smoothscroll
// animateIt.enableSmoothScroll(); enables smoothscroll
// animateIt.removeSmoothScroll(); removes smoothscroll altogether
// animateIt.leaveIframe(); same as when mouse leaves iframe

// WIGGLE ----------------------------------------------------------------------
// makes elements wiggle, can create multiple instances

// wiggle usage: animateIt.wiggle({ element: '#element' });

// required properties:
// element: element selector

// possible properties:
// speed = 0.1: larger is faster
// bounce = 0.01: larger is less overschoot
// distance = 20: max position switch distance in px
// switchPerSec = 2: number of position switches per second
// slowScroll = false: does it scroll along slowly?
// mousePush = false: does it move with the mouse?
// mousePushRate = 1: how much does it move with the mouse, larger = more
// scrollPush = false: does it move with the scroll?
// scrollPushRate = 1: how much does it move with the scroll, larger = more
// wiggle = true: does it wiggle?

// FOLLOW MOUSE  ---------------------------------------------------------------
// makes an element follow the mouse, supports 1 instance

// followMouse usage: animateIt.followMouse({ element: '#element' });

// required properties:
// element: element selector

// possible properties:
// speed = 1: larger is faster
// bounce = 0.1: larger is less overschoot
// rotate = false: does the element rotate?
// scrollSmear = false: is the mouse animated by scrolling?

// ISSUES ---------------------------------------------------------------------
// smoothscroll on iframe is not working on firefox

// CHANGELOG ------------------------------------------------------------------
// 3.0 added slowscroll
// 3.0.1 cleaner scripts and fixed ResizeObserver
// 3.1 added setting smooth scroll position (because it interferes with scrollTop)
// 3.1.2 eslint
// 3.2 fix firefox iframe scroll bug
// 3.3 add disable smooth scroll function
// 3.3.1 fix intertia hickup on touch devices
// 3.3.2 make arrays available to external functions
// 3.3.3 fix ios scroll bug
// 3.3.4 removed scrollbar on smooth scroll
// 3.3.5 nicer wait on new frame
// 3.4 add keyboard control for smooth scroll
// 3.4.1 fix scroll on iframe
// 3.4.2 block resize observer option
// 3.4.3 fix for leaving iframe without mouseout (for instance on page switch)
// 3.4.4 add focus handler, for scrolling to focus
// 3.4.5 fix focus handler: only work with keyboard focus
// 3.4.6 fix for enabling scroll when body was fixed
// 3.4.7 make scrollDiff available outside
// 3.4.8 add mousy hover for select elements
// 3.4.9 don't set scroll when not moving to save performance
// 3.4.10 do keyboard focus action only once
// 3.4.11 make scroller available outside, fix hash links on current page
// 3.4.12 fix inertia on touch devices
// 3.4.13 make scrollDiff available without smoothscroll
// 3.4.14 fix for enabling wheel when blocking smoothscroll
// 3.4.15 don't start mouse in center (often in the way)
// 3.4.16 fix for undefined href
// 3.4.17 add preventSearch option
// 3.4.18 fix search instead of preventSearch
// 4.0 remove jquery dependency
// 4.0.1 fix for non smoothscroll
// 4.1 new drag css
// 4.1.1 offsetTop fix
// 4.2 fix events
// 4.2.1 fix for scrolling when in form element
// 4.2.2 fix smooth scroll to keyboard focus
// 4.2.3 turn of smoothscroll when using pen/stylus on non touchscreen
// 4.3 cleanup of getAttribute code, add removeSmoothScroll
// 4.3.1 cleanup of keycode
// 4.4 hide scrollbar on smoothscroll when document is not in focus
// 4.4.1 only enable smoothscroll after window blur if it was enabled before
// 4.5 return objects to calling functions, add non wiggle option, add destroy wiggler option
// 4.5.1 fix blocked scroll after click on iframe
// 4.6 add difference vars for wigglers
// 4.6.1 only scroll to keyboard focus if not in view
// 4.7 add scrollbar
// 4.7.1 fix scrollbar height
// 4.7.2 add el from event function
// 4.7.3 add aria label to scrollbar
// 4.7.4 add check for scrollbar
// 4.7.5 don't always add fader--still, subpixel for firefox partly
// 4.7.6 fix slowscroll wiggle flash in view on resize
// 4.7.7 don't use scss vars
// 4.7.8 fix smoothscroll start if no history js
// 4.7.9 fix preventDefault error
// 4.8 add scrollPush option
// 4.8.1 fix unblock smoothscroll

export default class AnimateIt {
  constructor({frameFunction = ''} = '') {
    // console.log('AnimateIt 4.0');
    // user variables ------------------------------------------------------------
    // can be changed based on a media query for instance and read
    this.blockResizeObserver = false;
    this.arWigglers = [];
    this.arFollowers = [];
    this.scrollDiff = 0;
    this.scroller = undefined;

    // scope variables -----------------------------------------------------------
    let blockSmoothScroll = false;
    let mouseY = 0;
    let mouseX = 0;
    let mouseSmearY = 0;
    let mouseSmearX = 0;
    let mouseYDiff = 0;
    let mouseXDiff = 0;
    let mouseYPrevDiff = 0;
    let mouseXPrevDiff = 0;
    let mouseCounter = 0;
    let lastScrolledTop = 0;
    let lastScrolledLeft = 0;
    let scrollTop = 0;
    let scrollLeft = 0;
    let scrollTopDiff = 0;
    let scrollLeftDiff = 0;
    let scrollTimeout;

    let windowWidth = document.documentElement.clientWidth; // width without scrollbar
    let windowHeight = document.documentElement.clientHeight; // height minus navbar on touch
    let prevObserverWidth = 0;
    let prevObserverHeight = 0;
    let maxBodyScrollTop;
    let prevTouchY = 0;
    let prevBlockSmoothScroll = blockSmoothScroll;
    let onIframe = false;
    let iframeTimeout;
    let enteredFrame = false;
    let shiftDown = false;
    let focusSource = '';
    let touchendScrollDiff = 0;
    let touchMoved = false; // remembering if moved fixes hickup on tap during inertia
    let blockedScrollFromWindowBlur = false;

    let scrollbarEl;
    let scrollbarHeight = 0;
    let scrollDrag = false;
    let scrollDragPrevY = 0;

    const maxMouseDiff = 100;
    const maxScrollDiff = 100;

    let prevBodyPosition = '';

    let isTouch = false;
    if ('ontouchstart' in window || navigator.msMaxTouchPoints) isTouch = true;


    let blFirefox = false;
    // check for firefox
    if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) blFirefox = true;

    let prevWidth = 0;

    // functions -----------------------------------------------------------------
    const doFrame = () => {
      // console.log('doFrame', Date.now());
      // do fixReset when body becomes fixed or back again
      const bodyElement = document.querySelector('body');
      const bodyPosition = window.getComputedStyle(bodyElement).position;
      if (bodyPosition != prevBodyPosition) fixReset();
      prevBodyPosition = bodyPosition;

      wiggleFrameHandler();
      scrollFrameHandler();
      followFrameHandler();

      if (typeof frameFunction == 'function') frameFunction();

      requestAnimationFrame(doFrame);
    };

    // new smoothScroll ----------------------------------------------------------
    this.smoothScroll = ({speed = 0.1, bounce = 0.01, iframeScrollDelay = 1000}) => {
      if (isTouch) {
        document.documentElement.addEventListener('touchstart', scrollTouchStart);
        document.documentElement.addEventListener('touchmove', scrollTouchMove);
        document.documentElement.addEventListener('touchend', scrollTouchEnd);

        // fix preventDefault error
        document.body.style.touchAction = 'none';
      } else {
        // not touch

        // make sure body really doesn't scroll
        document.body.classList.add('body--animate-it-smoothscroll');
        document.body.classList.add('body--animate-it-smoothscroll-initialized');

        // create scrollbar element
        scrollbarEl = document.createElement('button');
        scrollbarEl.classList.add('animate-it-scrollbar');
        scrollbarEl.setAttribute('aria-label', 'Scrollbar');
        document.body.appendChild(scrollbarEl);

        document.body.addEventListener('mouseenter', handleIframeEnter, true);
        document.body.addEventListener('mouseout', event => {
          if (event.target.matches('iframe')) this.leaveIframe();
        });
      }

      const smoothScroll = {
        tempY: scrollTop,
        targetY: scrollTop,
        elY: scrollTop,
        speed,
        bounce,
        iframeScrollDelay,
      };
      // console.log(smoothScroll, scrollTop);
      this.scroller = smoothScroll;

      // must be after setting scroller
      resizeHandler(true);

      // make sure smoothscroll has same position as browser scroll
      // which is set after some time...
      setTimeout(() => {
        this.setScrollPosHard({pos: scrollTop});
      }, 100);
      
      return smoothScroll;
    };

    this.leaveIframe = () => {
      // console.log('leaveIframe');
      enteredFrame = false;
      clearTimeout(iframeTimeout); // make sure onIframe doesn't turn on

      // only if rested on iframe for iframeScrollDelay milliseconds
      if (onIframe) {
        this.setScrollPosHard({pos: scrollTop}); // sets scroll script to correct values
        onIframe = false; // can use smooth again
        document.body.classList.add('body--animate-it-smoothscroll');
      }

      if (typeof this.scroller !== 'object') return; // only do for smooth scroll

      if (blockedScrollFromWindowBlur) {
        document.body.classList.add('body--animate-it-smoothscroll');
        this.enableSmoothScroll();
        blockedScrollFromWindowBlur = false;
      }
    };

    const handleIframeEnter = event => {
      // console.log(event.target.matches('iframe'));
      // needed mousemove because enter sometimes didn't fire
      // reset timeout only once

      if (!enteredFrame
        && typeof this.scroller !== 'undefined'
        && event.target.matches('iframe')
      ) {
        clearTimeout(iframeTimeout);
        enteredFrame = true;

        // in timeout so it keeps its enterframe scroll for iframeScrollDelay milliseconds, after use browser scroll
        clearTimeout(iframeTimeout);
        iframeTimeout = setTimeout(() => {
          onIframe = true;
          document.body.classList.remove('body--animate-it-smoothscroll'); // make scroll possible
        }, this.scroller.iframeScrollDelay);
      }
    };

    this.disableSmoothScroll = () => {
      // console.log('disableSmoothScroll');
      blockSmoothScroll = true;
      if (scrollbarEl) scrollbarEl.disabled = true;
    };

    this.enableSmoothScroll = () => {
      // console.log('enableSmoothScroll');
      blockSmoothScroll = false;
      if (scrollbarEl) scrollbarEl.disabled = false;
    };

    this.removeSmoothScroll = () => {
      document.body.classList.remove('body--animate-it-smoothscroll');
      document.body.removeEventListener('mouseenter', handleIframeEnter, true);
      document.body.removeEventListener('mouseout', event => {});
      this.scroller = undefined;
    };

    const scrollFrameHandler = () => {
      // console.log('scrollFrameHandler', {blockSmoothScroll, onIframe});
      if (typeof this.scroller == 'undefined' || onIframe) return;

      // set scrollbar
      if (scrollbarEl) {
        scrollbarHeight = (windowHeight / document.documentElement.scrollHeight) * windowHeight;
        const scrollbarTop = (scrollTop / maxBodyScrollTop) * (windowHeight - scrollbarHeight);
        scrollbarEl.style.transform = 'translateY(' + scrollbarTop + 'px)';

        if (maxBodyScrollTop == 0) {
          scrollbarEl.classList.remove('animate-it-scrollbar--visible');
        } else {
          scrollbarEl.classList.add('animate-it-scrollbar--visible');
          scrollbarEl.style.height = scrollbarHeight + 'px';
        }
      }

      if (blockSmoothScroll) {
        prevBlockSmoothScroll = blockSmoothScroll;
        return;
      }

      // just unblocked
      if (prevBlockSmoothScroll != blockSmoothScroll && !blockSmoothScroll) {
        resizeHandler(); // get correct maxBodyScrollTop
        // console.log('unblocked', {scrollTop}, {maxBodyScrollTop});
        // set values to new scroll position
        this.scroller.tempY = scrollTop;
        this.scroller.targetY = scrollTop;
        this.scroller.elY = scrollTop;
      }

      prevBlockSmoothScroll = blockSmoothScroll;

      const distY = (this.scroller.targetY - this.scroller.elY) * this.scroller.speed;
      this.scroller.tempY += distY;

      // set max so no bounce on scroll edge
      if (this.scroller.tempY < 0) this.scroller.tempY = 0;
      if (this.scroller.tempY > maxBodyScrollTop) this.scroller.tempY = maxBodyScrollTop;

      // don't move if close to target, to save resources
      if (Math.abs(distY) < 0.01) return;

      // the sick calculation
      this.scroller.elY += this.scroller.bounce * (this.scroller.tempY + distY - this.scroller.elY);

      window.scroll({top: this.scroller.elY});

      // console.log('this.scroller.tempY', this.scroller.tempY);
      // console.log('this.scroller.elY', this.scroller.elY);
      // console.log('this.scroller.targetY', this.scroller.targetY);
    };

    this.setScrollPosHard = ({pos = 0}) => {
      // console.log('setScrollPosHard', {pos});
      if (typeof this.scroller == 'object') {
        this.scroller.tempY = pos;
        this.scroller.targetY = pos;
        this.scroller.elY = pos;
      }

      window.scroll({top: pos});
      setScrollerMax();
    };

    this.setScrollPosSoft = ({pos = 0}) => {
      // console.log('setScrollPosSoft', {pos});
      if (typeof this.scroller == 'object') this.scroller.targetY = pos;
      setScrollerMax();
    };

    // new wiggle ----------------------------------------------------------------
    this.wiggle = ({
      selector,
      speed = 0.1,
      bounce = 0.01,
      distance = 20,
      switchPerSec = 2,
      slowScroll = false,
      mousePush = false,
      mousePushRate = 1,
      scrollPush = false,
      scrollPushRate = 1,
      wiggle = true,
    }) => {
      const element = document.querySelector(selector);
      element.classList.add('animate-it-wiggler');

      scrollHandler();

      const parentEl = element.parentElement;
      if (slowScroll && parentEl && (parentEl.classList.contains('fader') || parentEl.classList.contains('fader2'))) {
        parentEl.classList.add('fader--still');
      }

      const wiggler = {
        element,
        startY: 0,
        tempY: slowScroll ? -scrollTop : 0,
        targetY: 0,
        elY: slowScroll ? -scrollTop : 0,
        prevElY: slowScroll ? -scrollTop : 0,
        diffY: 0,
        startX: 0,
        tempX: scrollLeft,
        targetX: 0,
        elX: scrollLeft,
        prevElX: scrollLeft,
        diffX: 0,
        speed,
        bounce,
        distance,
        switchEvery: 60 / switchPerSec,
        switchCounter: 0,
        slowScroll,
        mousePush,
        mousePushRate,
        scrollPush,
        scrollPushRate,
        wiggle,
      };

      this.arWigglers.push(wiggler);

      // must be after pushing wigglers
      resizeHandler(true);

      return wiggler;
    };

    const wiggleFrameHandler = () => {
      // console.log('wiggleFrameHandler');
      // if mouse is at same position
      if (mouseYPrevDiff == mouseYDiff && mouseXPrevDiff == mouseXDiff) {
        mouseCounter++;
      } else {
        mouseCounter = 0;
      }

      // on same position for 10 frames, for instance, outside of screen, reset mouse
      if (mouseCounter > 10) {
        mouseYDiff = 0;
        mouseXDiff = 0;
      }

      mouseYPrevDiff = mouseYDiff;
      mouseXPrevDiff = mouseXDiff;

      for (let i = 0; i < this.arWigglers.length; i++) {
        // save for cleaner code
        let item = this.arWigglers[i];

        if (item.wiggle) {
          item.switchCounter++;
          if (item.switchCounter > item.switchEvery) {
            item.switchCounter = 0;

            item.targetY = (Math.random() * item.distance) - (item.distance / 2);
            item.targetX = (Math.random() * item.distance) - (item.distance / 2);
          }
        }

        let scopeY;
        let scopeX;
        if (item.slowScroll) {
          scopeY = item.targetY - scrollTop;
          scopeX = item.targetX - scrollLeft;
        } else {
          scopeY = item.targetY;
          scopeX = item.targetX;
        }

        if (item.mousePush) {
          let mousePushAmount = item.mousePushRate;
          mousePushAmount /= Math.sqrt(item.speed);

          // console.log('mouseXDiff', mouseXDiff, 'mouseYDiff', mouseYDiff);

          scopeX += mouseXDiff * mousePushAmount;
          scopeY += mouseYDiff * mousePushAmount;
        }

        if (item.scrollPush) {
          let scrollPushAmount = item.scrollPushRate;
          scrollPushAmount /= Math.sqrt(item.speed);

          scopeX += scrollLeftDiff * scrollPushAmount;
          scopeY += scrollTopDiff * scrollPushAmount;
        }

        const distY = (scopeY - item.elY) * item.speed;
        item.tempY += distY;
        item.elY += item.bounce * (item.tempY + distY - item.elY);

        const distX = (scopeX - item.elX) * item.speed;
        item.tempX += distX;
        item.elX += item.bounce * (item.tempX + distX - item.elX);

        const transform3d = item.elX + 'px, ' + item.elY + 'px, 0';
        if (blFirefox) {
          item.element.style.transform = 'translate3d(' + transform3d + ') rotate(0.02deg)'; // rotate to add subpixel rendering
        } else {
          item.element.style.transform = 'translate3d(' + transform3d + ')';
        }

        item.diffX = item.elX - item.prevElX;
        item.diffY = item.elY - item.prevElY;

        item.prevElX = item.elX;
        item.prevElY = item.elY;

        // console.log('item.tempY', item.tempY);
        // console.log('item.elY', item.elY);
        // console.log('scopeY', scopeY);

        // put back
        this.arWigglers[i] = item;
      }
    };

    this.destroyWiggler = (wiggler, removeElement = false) => {
      // console.log('destroyWiggler', {wiggler, removeElement});
      const index = this.arWigglers.indexOf(wiggler);
      if (index > -1) {
        this.arWigglers.splice(index, 1);
        if (removeElement) wiggler.element.remove();
      }
    };

    // new followMouse -----------------------------------------------------------
    this.followMouse = ({
      selector,
      speed = 1,
      bounce = 0.1,
      rotate = false,
      scrollSmear = false,
    }) => {
      const element = document.querySelector(selector);

      const dot = document.createElement('div');
      dot.classList.add('animate-it-mouse__dot');
      element.appendChild(dot);

      for (let i = 0; i < 4; i++) {
        const corner = document.createElement('div');
        corner.classList.add('animate-it-mouse__corner');
        corner.classList.add('animate-it-mouse__corner--' + i);
        element.appendChild(corner);
      }

      element.classList.add('animate-it-mouse');
      if (scrollSmear) element.classList.add('animate-it-mouse--smear');

      document.body.classList.add('body--follow-mouse');

      const elFromEvent = (selector, event) => {
        const {target} = event;
        const ancestor = target.closest(selector);
        return ancestor || target.matches(selector);
      };

      const interactiveEls = 'button, a, input, label, textarea, select, *[role="button"';
      const frameEls = 'iframe, input[type="file"]';

      const handleMouseOver = event => {
        let el = elFromEvent(interactiveEls, event);
        if (el) element.classList.add('animate-it-mouse--hover');

        el = elFromEvent(frameEls, event);
        if (el) element.classList.add('animate-it-mouse--hidden');
      };

      const handleMouseOut = event => {
        let el = elFromEvent(interactiveEls, event);
        if (el) element.classList.remove('animate-it-mouse--hover');

        el = elFromEvent(frameEls, event);
        if (el) element.classList.remove('animate-it-mouse--hidden');
      };

      const handleMouseLeave = event => {
        const from = event.relatedTarget || event.toElement;
        if (!from || from.nodeName == 'HTML') {
          // stop your drag event here
          element.classList.add('animate-it-mouse--hidden');
        }
      };

      const handleMouseEnterDocument = event => {
        element.classList.remove('animate-it-mouse--hidden');
      };

      document.body.addEventListener('mouseover', handleMouseOver, true);
      document.body.addEventListener('mouseout', handleMouseOut, true);
      document.addEventListener('mouseleave', handleMouseLeave);
      document.addEventListener('mouseenter', handleMouseEnterDocument);

      mouseSmearY = (windowHeight / 2) + 100;
      mouseSmearX = windowWidth / 2;
      mouseY = mouseSmearY;
      mouseX = mouseSmearX;
      const startY = mouseY + scrollTop;
      const startX = mouseX + scrollLeft;

      const follower = {
        element,
        tempY: startY,
        targetY: startY,
        elY: startY,
        tempX: startX,
        targetX: startX,
        elX: startX,
        speed,
        bounce,
        rotate,
        scrollSmear,
      };

      this.arFollowers.push(follower);

      return follower;
    };

    const followFrameHandler = () => {
      for (let i = 0; i < this.arFollowers.length; i++) {
        // var for cleaner code
        let item = this.arFollowers[i];

        const startY = item.elY;
        const startX = item.elX;

        let scopeMouseX;
        let scopeMouseY;

        if (item.scrollSmear && !isTouch) {
          scopeMouseX = mouseSmearX;
          scopeMouseY = mouseSmearY;
        } else {
          scopeMouseX = mouseX;
          scopeMouseY = mouseY;
        }

        item.targetY = scopeMouseY;
        item.targetX = scopeMouseX;

        const distY = (item.targetY - item.elY) * item.speed;
        item.tempY += distY;
        item.elY += item.bounce * (item.tempY + distY - item.elY);

        const distX = (item.targetX - item.elX) * item.speed;
        item.tempX += distX;
        item.elX += item.bounce * (item.tempX + distX - item.elX);

        let transform3d;
        let distance;
        if (item.rotate) {
          let rotation = (Math.atan2(item.elY - startY, item.elX - startX) * 180) / Math.PI;
          distance = Math.sqrt(Math.abs(item.elY - startY) + Math.abs(item.elX - startX));

          // don't rotate based on small dinstances
          distance -= 2;
          if (distance < 0) {
            distance = 0;
            rotation = 0;
          }

          transform3d = item.elX + 'px, ' + item.elY + 'px, 0) rotate(' + rotation + 'deg';
          item.element.style.transform = 'translate3d(' + transform3d + ')';
          // const elements = item.element.getElementsByTagName('*:not(".animate-it-mouse__corner")');
        } else {
          distance = 0;
          transform3d = item.elX + 'px, ' + item.elY + 'px, 0';
          item.element.style.transform = 'translate3d(' + transform3d + ')';
        }

        // set dot rotate
        const dot = item.element.querySelector('.animate-it-mouse__dot');
        dot.style.transform = 'scale(' + (distance + 1) + ', 1)';

        this.arFollowers[i] = item;
      }
    };

    const mousedownHandler = event => {
      // console.log('mousedownHandler', event.target);
      focusSource = 'mouse';

      const el = elFromEvent('.animate-it-scrollbar', event);
      if (el && !isTouch) {
        scrollDrag = true;
        scrollDragPrevY = event.clientY;
        el.classList.add('animate-it-scrollbar--drag');
      }
    };

    const mousemoveHandler = event => {
      if (!isTouch) {
        mouseSmearX = event.pageX;
        mouseSmearY = event.pageY;

        inputPositionChange(event.clientX, event.clientY);

        if (scrollDrag) {
          // console.log({scrollDrag, scrollDragPrevY});
          const diff = event.clientY - scrollDragPrevY;
          const targetDiff = diff * (maxBodyScrollTop / (windowHeight - scrollbarHeight));

          this.scroller.targetY += targetDiff;
          setScrollerMax();

          scrollDragPrevY = event.clientY;
        }
      }
    };

    const mouseupHandler = event => {
      // console.log('mouseupHandler');
      scrollDrag = false;
      if (scrollbarEl) scrollbarEl.classList.remove('animate-it-scrollbar--drag');
    };

    const touchHandler = event => {
      let evt = (typeof event.originalEvent === 'undefined') ? event : event.originalEvent;
      let touch = evt.touches[0] || evt.changedTouches[0];

      inputPositionChange(touch.pageX, touch.pageY);
    };

    const inputPositionChange = (x, y) => {
      const tempX = mouseX;
      const tempY = mouseY;

      mouseX = x;
      mouseY = y;

      // console.log({mouseY, mouseX});
      mouseXDiff = mouseX - tempX;
      mouseYDiff = mouseY - tempY;

      limitMouseDiff();
    };

    const limitMouseDiff = () => {
      // limit mousediff for too crazy movements
      let multiplier;
      if (mouseXDiff > maxMouseDiff) {
        multiplier = maxMouseDiff / mouseXDiff;
        mouseXDiff = maxMouseDiff;
        mouseYDiff *= multiplier;
      }

      if (mouseYDiff > maxMouseDiff) {
        multiplier = maxMouseDiff / mouseYDiff;
        mouseYDiff = maxMouseDiff;
        mouseXDiff *= multiplier;
      }

      if (mouseXDiff < -maxMouseDiff) {
        multiplier = Math.abs(maxMouseDiff / mouseXDiff);
        mouseXDiff = -maxMouseDiff;
        mouseYDiff *= multiplier;
      }

      if (mouseYDiff < -maxMouseDiff) {
        multiplier = Math.abs(maxMouseDiff / mouseYDiff);
        mouseYDiff = -maxMouseDiff;
        mouseXDiff *= multiplier;
      }
    };

    // touch handlers for scroll and reading mouse movement
    const scrollTouchStart = event => {
      let evt = (typeof event.originalEvent === 'undefined') ? event : event.originalEvent;
      let touch = evt.touches[0] || evt.changedTouches[0];

      // save start
      prevTouchY = touch.pageY;
      touchMoved = false;
    };

    const scrollTouchMove = event => {
      // prevent regular scroll
      event.preventDefault();

      touchMoved = true;

      // save difference to target
      let evt = (typeof event.originalEvent === 'undefined') ? event : event.originalEvent;
      let touch = evt.touches[0] || evt.changedTouches[0];
      const touchY = touch.pageY;

      this.scrollDiff = prevTouchY - touchY;
      touchendScrollDiff = this.scrollDiff; // separate var because scrollDiff can be 0 from reset

      prevTouchY = touchY;

      if (typeof this.scroller === 'object') this.scroller.targetY += this.scrollDiff;

      setScrollerMax();
    };

    const scrollTouchEnd = event => {
      // console.log({touchMoved}, touchendScrollDiff);
      if (!touchMoved) return;
      this.scrollDiff = 10 * touchendScrollDiff; // inertia, 10 just worked

      if (typeof this.scroller === 'object') this.scroller.targetY += this.scrollDiff;
      setScrollerMax();
    };

    const scrollTouchWheel = event => {
      if (event.target.matches('iframe')) return;

      this.scrollDiff = event.deltaY;

      if (typeof this.scroller === 'object' && !blockSmoothScroll) {
        // prevent regular scroll
        event.preventDefault();
        event.stopPropagation();

        // save mousewheel to target
        this.scroller.targetY += this.scrollDiff;
      }

      setScrollerMax();
    };

    const scrollPointerDown = event => {
      if (typeof this.scroller !== 'object') return;
      if (event.pointerType == 'pen' && !isTouch) this.removeSmoothScroll(); // safari returns 'mouse' :(
    };

    const setScrollerMax = () => {
      // console.log('setScrollerMax');
      requestAnimationFrame(() => {
        this.scrollDiff = 0;
      });

      if (typeof this.scroller !== 'object') return;

      if (this.scroller.targetY < 0) this.scroller.targetY = 0;
      if (this.scroller.targetY > maxBodyScrollTop) this.scroller.targetY = maxBodyScrollTop;
    };

    // other functions -----------------------------------------------------------
    const resizeHandler = force => {
      // console.log('resizeHandler');
      windowWidth = document.documentElement.clientWidth; // width without scrollbar
      windowHeight = document.documentElement.clientHeight; // height minus navbar on touch

      // calculate max scroll for slowscroll
      const windowInnerHeight = window.innerHeight; // height minus current navbar on touch
      const documentHeight = document.documentElement.scrollHeight; // entire document height
      if (getComputedStyle(document.body).position !== 'fixed') { // fixed body has no scroll
        maxBodyScrollTop = documentHeight - windowInnerHeight;
      }

      // console.log({ windowInnerHeight }, { documentHeight }, { maxBodyScrollTop });
      // check if resize width or forced
      if (windowWidth == prevWidth && force != true) return;

      prevWidth = windowWidth;

      // console.log('resizeHandler', 'this.arWigglers.length', this.arWigglers.length);
      for (let i = 0; i < this.arWigglers.length; i++) {
        if (this.arWigglers[i].slowScroll) {
          const el = this.arWigglers[i].element;
          el.classList.remove('animate-it-wiggler--slowscroll');

          el.style.top = '';
          el.style.left = '';
          el.style.width = '';
          el.style.transform = '';

          el.style.top = (el.getBoundingClientRect().top + scrollTop) + 'px';
          el.style.left = (el.getBoundingClientRect().left + scrollLeft) + 'px';
          el.style.width = el.getBoundingClientRect().width + 'px';

          el.classList.add('animate-it-wiggler--slowscroll');
        }
      }

      scrollHandler();
      wiggleFrameHandler();
    };

    const scrollHandler = () => {
      // console.log('scrollHandler');

      scrollTop = window.scrollY || document.documentElement.scrollTop;
      scrollLeft = window.scrollX || document.documentElement.scrollLeft;

      scrollLeftDiff = lastScrolledLeft - scrollLeft;
      scrollTopDiff = lastScrolledTop - scrollTop;

      if (scrollLeftDiff > maxScrollDiff) scrollLeftDiff = maxScrollDiff;
      if (scrollLeftDiff < -maxScrollDiff)  scrollLeftDiff = -maxScrollDiff;

      if (scrollTopDiff > maxScrollDiff)  scrollTopDiff = maxScrollDiff;
      if (scrollTopDiff < -maxScrollDiff) scrollTopDiff = -maxScrollDiff;

      lastScrolledLeft = scrollLeft;
      lastScrolledTop = scrollTop;

      if (!isTouch) {
        if (lastScrolledLeft !== scrollLeft) {
          mouseSmearX -= lastScrolledLeft;
          lastScrolledLeft = scrollLeft;
          mouseSmearX += lastScrolledLeft;
        }

        if (lastScrolledTop !== scrollTop) {
          mouseSmearY -= lastScrolledTop;
          lastScrolledTop = scrollTop;
          mouseSmearY += lastScrolledTop;
        }
      }

      // fix for hard setting scroll
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        scrollLeftDiff = 0;
        scrollTopDiff = 0;
      }, 100);
    };

    const formElementFocused = event => {
      const el = event.target;
      return el.tagName == 'INPUT' || el.tagName == 'TEXTAREA' || el.tagName == 'SELECT' || el.isContentEditable;
    };

    // keyPresshandler
    const keyPressHandler = event => {
      // console.log('keyPressHandler', event);

      if (typeof this.scroller !== 'object' || formElementFocused(event)) return;

      if (event.code === 'Space') { // space
        this.scrollDiff = shiftDown ? -windowHeight : windowHeight;
        this.scroller.targetY += this.scrollDiff;
      }

      setScrollerMax();
    };

    // keyDownHandler
    const keyDownHandler = event => {
      // console.log('keyDownHandler', event);

      focusSource = 'keyboard';

      if (typeof this.scroller !== 'object' || formElementFocused(event)) return;

      if (event.key === 'ArrowUp') { // up
        this.scrollDiff = -100;
      } else if (event.key === 'ArrowDown') { // down
        this.scrollDiff = 100;
      } else if (event.key === 'Shift') {
        shiftDown = true;
      }

      this.scroller.targetY += this.scrollDiff;

      setScrollerMax();
    };

    // keyUpHandler
    const keyUpHandler = event => {
      // console.log('keyUpHandler', event);

      if (event.key === 'Shift') shiftDown = false;
    };

    // focusHandler
    const focusHandler = event => {
      // console.log('focusHandler', {focusSource});
      // FYI focus handler isn't called in slick sliders for some reason
      // removing negative tab-index doesn't fix this and creates other issues

      if (
        typeof this.scroller !== 'object' // has no smooth scroll
        || focusSource == 'mouse' // from mouse
        || event.target != document.activeElement // do only once
      ) return;

      const el = event.target;

      // must be in timeout, because keyoard focus scroll can't be prevented
      // and getBoundingClientRect is wrong
      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        // check if element in view, if not, scroll to it
        if (rect.top < 0 || rect.bottom > windowHeight) scrollToElement(el);
      });
    };

    // linkHandler
    const linkHandler = event => {
      if (typeof this.scroller !== 'object') return; // only do for smooth scroll

      const href = event.target.getAttribute('href');
      if (!href) return;
      const cleanHref = href.split('?')[0].split('#')[0];
      const cleanPath = window.location.href.split('?')[0].split('#')[0];

      // console.log('linkHandler', {href}, {cleanHref}, {cleanPath});

      // check if clicked href is on the current page
      if ((cleanHref === cleanPath || cleanHref === '') && href.indexOf('#') !== -1) {
        event.preventDefault();

        const hrefHash = href.split('#')[1];
        scrollToElement(document.getElementById(hrefHash));
      }
    };

    const scrollToElement = el => {
      const offsetTop = el.getBoundingClientRect().top + scrollTop;
      const center = offsetTop - (windowHeight / 2);
      this.setScrollPosSoft({pos: center});
    };

    const blurWindowHandler = () => {
      if (typeof this.scroller !== 'object') return; // only do for smooth scroll

      if (blockSmoothScroll == false) {
        blockedScrollFromWindowBlur = true;
        this.disableSmoothScroll();
        document.body.classList.remove('body--animate-it-smoothscroll');
      }
    };

    const focusWindowHandler = () => {
      if (typeof this.scroller !== 'object') return; // only do for smooth scroll

      if (blockedScrollFromWindowBlur) {
        document.body.classList.add('body--animate-it-smoothscroll');
        this.enableSmoothScroll();
        blockedScrollFromWindowBlur = false;
      }
    };

    const fixReset = () => {
      // console.log('fixReset');

      scrollTop = window.scrollY || document.documentElement.scrollTop;
      scrollLeft = window.scrollX || document.documentElement.scrollLeft;

      let i;
      for (i = 0; i < this.arWigglers.length; i++) {
        if (this.arWigglers[i].slowScroll) {
          this.arWigglers[i].startY = 0;
          this.arWigglers[i].tempY = -scrollTop;
          this.arWigglers[i].targetY = 0;
          this.arWigglers[i].elY = -scrollTop;
          this.arWigglers[i].startX = 0;
          this.arWigglers[i].tempX = scrollLeft;
          this.arWigglers[i].targetX = 0;
          this.arWigglers[i].elX = scrollLeft;
        }
      }

      for (i = 0; i < this.arFollowers.length; i++) {
        let startY;
        if (this.arFollowers[i].scrollSmear && !isTouch) {
          startY = mouseY + scrollTop;
        } else {
          startY = mouseY;
        }

        const startX = mouseX + scrollLeft;

        this.arFollowers[i].tempY = startY;
        this.arFollowers[i].targetY = startY;
        this.arFollowers[i].elY = startY;
        this.arFollowers[i].tempX = startX;
        this.arFollowers[i].targetX = startX;
        this.arFollowers[i].elX = startX;
      }
    };

    // check for resizing of holders --------------------------------
    const observerResize = () => {
      // console.log(
      //   'observerResize',
      //   windowWidth,
      //   prevObserverWidth,
      //   windowHeight,
      //   prevObserverHeight
      // );
      // prevent double resize when resizing window
      if (
        (windowWidth != prevObserverWidth || windowHeight != prevObserverHeight)
        && prevObserverWidth != 0
      ) {
        prevObserverWidth = windowWidth;
        prevObserverHeight = windowHeight;
        // console.log('no resizeHandler from observer');
        return;
      }

      prevObserverWidth = windowWidth;
      prevObserverHeight = windowHeight;

      // console.log('resizeHandler from observer');
      resizeHandler(true);
    };

    const observerHandler = () => {
      // after possible site specific resize calculations
      if (!this.blockResizeObserver) requestAnimationFrame(observerResize);
    };

    const elFromEvent = (selector, event) => {
      const {target} = event;
      const ancestor = target.closest(selector);
      return ancestor || target.matches(selector);
    };

    // listeners and loaders
    doFrame();

    // for wiggle/follow
    document.documentElement.addEventListener('touchmove', touchHandler);
    document.documentElement.addEventListener('touchstart', touchHandler);
    document.documentElement.addEventListener('mousemove', mousemoveHandler);

    // for smoothscroll and reading touch movement/wheel
    document.documentElement.addEventListener('wheel', scrollTouchWheel, {capture: true, passive: false});
    document.documentElement.addEventListener('pointerdown', scrollPointerDown);

    window.addEventListener('resize', () => {
      // after possible site specific resize calculations
      requestAnimationFrame(resizeHandler);
    });
    document.addEventListener('scroll', scrollHandler);

    // keys for smoothscroll
    document.addEventListener('keydown', keyDownHandler);
    document.addEventListener('keyup', keyUpHandler);
    document.addEventListener('keypress', keyPressHandler);

    // focus for smoothscroll
    document.documentElement.addEventListener('mousedown', mousedownHandler);
    document.documentElement.addEventListener('mouseup', mouseupHandler);
    document.documentElement.addEventListener('focus', focusHandler, true);

    // anchor click for smoothscroll
    document.addEventListener('click', event => {
      if (event.target.matches('a')) linkHandler(event);
    });

    // this makes search possible
    window.addEventListener('blur', blurWindowHandler);
    window.addEventListener('focus', focusWindowHandler);

    // observer might not trigger, so use load event too
    window.addEventListener('load', () => {
      // console.log('resizeHandler from load');
      resizeHandler(true);
    });

    // add an observer, so the resizer is called when body size changes
    const el = document.querySelector('body');
    new ResizeObserver(observerHandler).observe(el);

    // do scroll and resize so values are set
    resizeHandler();
    scrollHandler();
  }
}
